import { Container, Grid, LinearProgress, Typography } from "@mui/material";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import {
  ProgrammeActions,
  selectAllProgrammeActions,
  setQuickAssessmentFinished,
  useAppDispatch,
  useAppSelector,
} from "model";
import { useHistory } from "react-router-dom";

import FolderOpen from "@mui/icons-material/FolderOpen";
import { IconButton } from "@mui/material";
import {
  fetchAllQuickAssessmentResults,
  NO_PROGRAMM_ID,
  selectQuickAssessmentResultsById,
} from "model/quickAssessmentSlice";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import CowInCircle from "./cowInCircle2";
import NavigationLock from "./NavigationLock";
import NoProgrammeDrawerElement from "./NoProgrammeDrawerElement";

const REFRESH_INTERVAL = 1 * 1000;

export default function ProgrammesDrawer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const quickAssessmentStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).quickAssessmentStarted
  );
  const programmesActions = useAppSelector((state) =>
    selectAllProgrammeActions(state)
  );
  const sortedProgrammesActions = programmesActions.sort(
    (a: ProgrammeActions, b: ProgrammeActions) => {
      return a.programmeName > b.programmeName ? 1 : -1;
    }
  );

  const history = useHistory();
  const handleBackToFarm = () => {
    history.push(`/file-management-overview`);
  };
  const loading = useAppSelector(
    (state) => state.programmeActions.loading === "pending"
  );

  useEffect(() => {
    const unsetQuickAssessmentStarted = history.listen((e) => {
      if (e.pathname !== `/programmes`) {
        // Returning false allows navigation, returning a string shows a prompt
        dispatch(setQuickAssessmentFinished(true));
      }
    });

    return function cleanup() {
      unsetQuickAssessmentStarted();
    };
  }, [dispatch, history]);

  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (quickAssessmentStarted.quickAssessmentStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickAssessmentStarted.quickAssessmentStarted]);

  const quickAssessmentResults = useAppSelector((state) =>
    selectQuickAssessmentResultsById(state, NO_PROGRAMM_ID)
  );
  const hasFilledOutQuickAssessment = !(
    Object.keys(quickAssessmentResults?.results ?? {}).length === 0
  );
  const programmeActionsQuestionnaireFilledOutIds = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).programmeActionsQuestionnaireFilledOutIds
  );
  const filledOutButResultNotAvailableYet =
    programmeActionsQuestionnaireFilledOutIds.programmeActionsQuestionnaireFilledOutIds.includes(
      NO_PROGRAMM_ID
    ) && !hasFilledOutQuickAssessment;
  const loadingQuickAssessment = useAppSelector(
    (state) => state.quickAssessmentResults.loading === "pending"
  );

  useEffect(() => {
    if (filledOutButResultNotAvailableYet) {
      const reloadProgrammesAndActions = async () => {
        if (!loading) {
          await dispatch(fetchAllQuickAssessmentResults());
        }
        return;
      };
      const refreshInterval = setInterval(
        reloadProgrammesAndActions,
        REFRESH_INTERVAL
      );
      // clear interval when there no fields with invalid data
      if (hasFilledOutQuickAssessment) {
        clearInterval(refreshInterval);
      }
      return () => clearInterval(refreshInterval);
    }
  }, [
    dispatch,
    filledOutButResultNotAvailableYet,
    loading,
    hasFilledOutQuickAssessment,
  ]);
  console.log("wth");
  if (loading || loadingQuickAssessment || filledOutButResultNotAvailableYet) {
    return <LinearProgress />;
  }

  console.log(
    `quickAssessmentStarted.quickAssessmentStarted ${quickAssessmentStarted.quickAssessmentStarted}`
  );

  return (
    <>
      <NavigationLock when={quickAssessmentStarted.quickAssessmentStarted} />
      <PageTitleBar
        title={t("programmes.toolbar.title")}
        showDivider
        children={
          <IconButton
            onClick={handleBackToFarm}
            aria-controls="profile-menu"
            aria-haspopup="true"
          >
            <FolderOpen />
          </IconButton>
        }
      />
      {sortedProgrammesActions.length === 0 && <NoProgrammeDrawerElement />}
      {sortedProgrammesActions.length > 0 && (
        <Container
          maxWidth="sm"
          disableGutters
          sx={{
            bgcolor: "#F7FCF1",
            pt: 2,
            paddingTop: "calc(env(safe-area-inset-top) + 16px)",
            height: "calc(100vh - 120px)",
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          <Grid
            container
            spacing={0}
            sx={{ padding: 0, paddingTop: 2 }}
            display="flex"
            flexDirection={"row"}
            alignItems="baseline"
            justifyContent="center"
            width={"100%"}
          >
            {sortedProgrammesActions.map((programme) => {
              return (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  display="flex"
                  alignItems="baseline"
                  key={programme.guid}
                  onClick={() => {
                    history.push(`/programme-details/${programme.guid}`);
                  }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: 0,
                    p: 3,
                    mb: 2,
                    cursor: "pointer",
                    boxShadow: 0,
                    flex: 1, // Take remaining space
                    width: "100vh", // Increased height - less space reserved
                    maxWidth: "100vh", // Increased height - less space reserved
                  }}
                >
                  {<ProgrammeButton2 programme={programme} />}
                </Grid>
              );
            })}
          </Grid>
        </Container>
      )}
    </>
  );
}

const ProgrammeButton2 = ({ programme }: { programme: ProgrammeActions }) => {
  return (
    <>
      <div
        style={{
          gap: "20px",
          paddingRight: "20px",
        }}
      >
        <CowInCircle />
      </div>
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        style={{
          fontSize: "22px",
          fontWeight: 600,
          margin: 0,
          color: "#000",
          flex: 1,
        }}
      >
        {programme.programmeName}
      </Typography>
    </>
  );
};
